import { useMemo, useState } from 'react';
import { CheckCircle2, ShieldCheck, ShieldOff, UserPlus, Users } from 'lucide-react';

import type {
  AdminUserListItem,
  AdminUserListParams,
  UserRole,
} from '../../../api/adminUsers';
import { DataTablePagination } from '../../../components/DataTablePagination';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Combobox } from '../../../components/ui/combobox';
import { EmptyState } from '../../../components/ui/EmptyState';
import { ErrorState } from '../../../components/ui/ErrorState';
import { PageHeader } from '../../../components/ui/PageHeader';
import { SearchInput } from '../../../components/ui/search-input';
import { Spinner } from '../../../components/ui/spinner';
import { StatCard } from '../../../components/ui/StatCard';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { useAuth } from '../../../hooks/useAuth';
import { useAdminUsersList } from '../../../hooks/admin/useAdminUsers';
import { classifyError } from '../../../lib/errors';
import { EditUserDialog } from './EditUserDialog';
import { InviteUserDialog } from './InviteUserDialog';
import { UserActionsMenu } from './UserActionsMenu';

const ROLE_LABEL: Record<UserRole, string> = {
  admin: 'Administrador',
  advogado: 'Advogado',
  suporte: 'Suporte',
};

const ROLE_FILTER_OPTIONS = [
  { value: '', label: 'Todas as roles' },
  { value: 'admin', label: 'Administrador' },
  { value: 'advogado', label: 'Advogado' },
  { value: 'suporte', label: 'Suporte' },
];

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'Todos os status' },
  { value: 'active', label: 'Ativos' },
  { value: 'blocked', label: 'Bloqueados' },
];

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR');
}

function RoleBadge({ role }: { role: UserRole | null }) {
  if (!role) return <Badge variant="outline">Sem role</Badge>;
  const variant = role === 'admin' ? 'default' : 'secondary';
  return <Badge variant={variant}>{ROLE_LABEL[role]}</Badge>;
}

function StatusBadge({ user }: { user: AdminUserListItem }) {
  if (!user.is_active) return <Badge variant="destructive">Bloqueado</Badge>;
  if (!user.email_verified) return <Badge variant="outline">Convite pendente</Badge>;
  return <Badge variant="secondary">Ativo</Badge>;
}

function EmailVerifiedBadge({ verified }: { verified: boolean }) {
  if (verified) return <Badge variant="secondary">Sim</Badge>;
  return <Badge variant="outline">Não</Badge>;
}

export default function UsuariosPage() {
  const { user: me } = useAuth();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [role, setRole] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editing, setEditing] = useState<AdminUserListItem | null>(null);

  const params = useMemo<AdminUserListParams>(() => {
    const p: AdminUserListParams = { page, page_size: pageSize };
    if (search.trim()) p.search = search.trim();
    if (role) p.role = role as UserRole;
    if (statusFilter === 'active') p.active = true;
    if (statusFilter === 'blocked') p.active = false;
    return p;
  }, [page, pageSize, search, role, statusFilter]);

  const { data, isLoading, isError, error, refetch } = useAdminUsersList(params);

  const items = useMemo(() => data?.items ?? [], [data?.items]);
  const total = data?.total ?? 0;
  const totalAdmins = data?.total_admins ?? 0;
  const totalBlocked = data?.total_blocked ?? 0;
  const totalPending = data?.total_pending ?? 0;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Usuários"
        subtitle="Convite, edição de papéis, bloqueio e exclusão (sincronizado com Auth0)."
        icon={<Users />}
        actions={
          <Button onClick={() => setInviteOpen(true)}>
            <UserPlus className="h-4 w-4" /> Convidar
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard title="Total" value={total} icon={<Users />} />
        <StatCard title="Administradores" value={totalAdmins} icon={<ShieldCheck />} />
        <StatCard title="Bloqueados" value={totalBlocked} icon={<ShieldOff />} />
        <StatCard
          title="Convite pendente"
          value={totalPending}
          icon={<CheckCircle2 />}
        />
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex-1">
          <SearchInput
            value={search}
            onChange={handleSearchChange}
            placeholder="Buscar por nome ou email…"
          />
        </div>
        <div className="w-full sm:w-48">
          <Combobox
            value={role}
            onValueChange={(v) => {
              setRole(v);
              setPage(1);
            }}
            options={ROLE_FILTER_OPTIONS}
            placeholder="Filtrar por role"
            searchPlaceholder="Buscar role…"
            emptyMessage="Nenhuma role"
          />
        </div>
        <div className="w-full sm:w-48">
          <Combobox
            value={statusFilter}
            onValueChange={(v) => {
              setStatusFilter(v);
              setPage(1);
            }}
            options={STATUS_FILTER_OPTIONS}
            placeholder="Filtrar por status"
            searchPlaceholder="Buscar status…"
            emptyMessage="Nenhum status"
          />
        </div>
      </div>

      {isLoading && (
        <div className="flex justify-center py-12">
          <Spinner size={32} label="Carregando usuários" />
        </div>
      )}

      {isError && !isLoading && (
        <ErrorState
          title="Não foi possível carregar os usuários"
          description={classifyError(error).message}
          actions={[{ label: 'Tentar novamente', onClick: () => refetch() }]}
        />
      )}

      {!isLoading && !isError && items.length === 0 && (
        <EmptyState
          icon={<Users />}
          title="Nenhum usuário encontrado"
          description={
            search || role || statusFilter !== 'all'
              ? 'Ajuste os filtros ou limpe a busca.'
              : 'Comece convidando o primeiro usuário.'
          }
          action={
            !search && !role && statusFilter === 'all'
              ? { label: 'Convidar usuário', onClick: () => setInviteOpen(true) }
              : undefined
          }
        />
      )}

      {!isLoading && !isError && items.length > 0 && (
        <div className="rounded-lg border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="hidden sm:table-cell">Role</TableHead>
                <TableHead className="hidden sm:table-cell">Status</TableHead>
                <TableHead className="hidden md:table-cell">E-mail verificado</TableHead>
                <TableHead className="hidden lg:table-cell">Cadastrado em</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((u) => (
                <TableRow key={u.id}>
                  <TableCell className="font-medium">
                    <div className="flex flex-col gap-0.5">
                      <span>{u.nome}</span>
                      <div className="flex flex-wrap items-center gap-1.5 sm:hidden">
                        <RoleBadge role={u.role} />
                        <StatusBadge user={u} />
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{u.email}</TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <RoleBadge role={u.role} />
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <StatusBadge user={u} />
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <EmailVerifiedBadge verified={u.email_verified} />
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-muted-foreground">
                    {formatDate(u.created_at)}
                  </TableCell>
                  <TableCell>
                    <UserActionsMenu
                      user={u}
                      isSelf={me?.id === u.id}
                      onEdit={() => setEditing(u)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <DataTablePagination
            page={page}
            pageSize={pageSize}
            total={total}
            pages={pages}
            onPageChange={setPage}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setPage(1);
            }}
          />
        </div>
      )}

      <InviteUserDialog open={inviteOpen} onOpenChange={setInviteOpen} />
      <EditUserDialog user={editing} onClose={() => setEditing(null)} />
    </div>
  );
}
